import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Trash2, BookOpen, Sparkles, TrendingUp, Wand2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';
import { toast } from 'sonner';

const AI_SUGGEST_KEY = 'smarto.bankCodeDict.aiSuggest';

/**
 * iter444 — Bank Code Dictionary editor.
 *
 * Lets a tenant accountant view all built-in bank-statement codes
 * (SARIE, MADA-POS, IBFT, …) and teach Smarto new ones or override
 * the default labels. Backed by `/api/banking/code-dictionary` (CRUD).
 *
 * Opens via the small "decoded label" badge in the unlinked-txn UI —
 * `defaultNewCode` / `defaultNewLabel` can be pre-filled from the row
 * the user clicked.
 */
export default function BankCodeDictionaryDialog({
  open,
  onClose,
  defaultNewCode = '',
  defaultNewLabel = '',
  onDirty,                 // called whenever overrides change (so caller can refetch)
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]); // [{code,label,source}]
  const [filter, setFilter] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  // iter445 — trending unknown codes (top tokens from unlinked statements
  // that the decoder doesn't recognise yet — one click pre-fills the
  // add row so the accountant can teach Smarto on the spot).
  const [trending, setTrending] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  // iter445c — opt-in AI label suggester. Persisted per-browser so the
  // toggle remembers between sessions.
  const [aiSuggest, setAiSuggest] = useState(() => {
    try { return localStorage.getItem(AI_SUGGEST_KEY) === '1'; } catch { return false; }
  });
  const [aiBusy, setAiBusy] = useState(false);
  const toggleAi = (next) => {
    setAiSuggest(next);
    try { localStorage.setItem(AI_SUGGEST_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  };
  // iter445d — "Suggest all" inline preview: list of {token,label,picked}
  // shown right below the chips. Empty array => preview closed.
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchRows, setBatchRows] = useState([]); // [{token, label, picked, ok}]
  // iter446 — bank-account scope for the trending list. Empty string =>
  // "All banks". Fetched on dialog open so the user doesn't need to be
  // on a page that already has the list loaded.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankScope, setBankScope] = useState('');
  // iter447 — `by_bank` map (returned by GET /code-dictionary) and a
  // memoized "id → label" lookup for the new Scope column + accordion.
  const [byBank, setByBank] = useState({});

  const fetchBankAccounts = async () => {
    try {
      const r = await api.get('/bank-accounts');
      setBankAccounts(Array.isArray(r.data) ? r.data : []);
    } catch {
      setBankAccounts([]);
    }
  };

  const bankLabel = (bid) => {
    if (!bid) return 'All banks';
    const fromState = bankAccounts.find(a => a.id === bid);
    if (fromState) return fromState.bank_name || fromState.account_name || fromState.account_number || bid.slice(0, 8);
    return bid.slice(0, 8);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = bankScope ? { bank_account_id: bankScope } : {};
      const r = await api.get('/banking/code-dictionary', { params });
      setEntries(r.data?.entries || []);
      setByBank(r.data?.by_bank || {});
      // Backend now returns lightweight bank_accounts in the response;
      // prefer them if the standalone /bank-accounts call hasn't run yet.
      if ((r.data?.bank_accounts || []).length && bankAccounts.length === 0) {
        setBankAccounts(r.data.bank_accounts.map(b => ({ id: b.id, bank_name: b.label })));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bank-code dictionary');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrending = async (scopeOverride) => {
    setTrendingLoading(true);
    try {
      const params = { days: 365, limit: 5 };
      const scope = scopeOverride !== undefined ? scopeOverride : bankScope;
      if (scope) params.bank_account_id = scope;
      const r = await api.get('/banking/code-dictionary/trending-unknown', { params });
      setTrending(r.data?.tokens || []);
    } catch {
      // Non-fatal — the dictionary remains usable without trending.
      setTrending([]);
    } finally {
      setTrendingLoading(false);
    }
  };

  // iter463 — Toggle whether a code should count as a POS settlement
  // in the Sales-vs-Bank widget. Optimistic UI: flip the flag locally,
  // then PUT to the backend. On failure we revert + toast.
  const togglePos = async (code, nextFlag) => {
    setEntries((prev) => prev.map(r => r.code === code ? { ...r, is_pos_settlement: nextFlag } : r));
    try {
      await api.put(`/banking/code-dictionary/pos-settlement/${encodeURIComponent(code)}`, {
        flagged: nextFlag,
      });
      toast.success(nextFlag ? 'Counts as POS settlement' : 'Not a POS settlement');
    } catch (e) {
      // Revert
      setEntries((prev) => prev.map(r => r.code === code ? { ...r, is_pos_settlement: !nextFlag } : r));
      toast.error(e?.response?.data?.detail || 'Failed to update POS flag');
    }
  };

  // iter464 — Toggle the refund/chargeback flag. Same pattern as
  // togglePos. Refund credits are subtracted from the bank-total in
  // the Sales-vs-Bank widget so card refunds don't inflate variance.
  const toggleRefund = async (code, nextFlag) => {
    setEntries((prev) => prev.map(r => r.code === code ? { ...r, is_refund: nextFlag } : r));
    try {
      await api.put(`/banking/code-dictionary/refund/${encodeURIComponent(code)}`, {
        flagged: nextFlag,
      });
      toast.success(nextFlag ? 'Counts as refund / chargeback' : 'Not a refund');
    } catch (e) {
      setEntries((prev) => prev.map(r => r.code === code ? { ...r, is_refund: !nextFlag } : r));
      toast.error(e?.response?.data?.detail || 'Failed to update refund flag');
    }
  };

  useEffect(() => {
    if (!open) return;
    setNewCode((defaultNewCode || '').toUpperCase());
    setNewLabel(defaultNewLabel || '');
    setFilter('');
    setBankScope('');
    fetchAll();
    fetchBankAccounts();
    fetchTrending('');
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, defaultNewCode, defaultNewLabel]);

  const onBankScopeChange = (next) => {
    setBankScope(next);
    setBatchRows([]); // close any stale preview when the scope changes
    fetchTrending(next);
    // iter446b — reload the dictionary so the list reflects which
    // entries belong to the new scope.
    (async () => {
      try {
        const params = next ? { bank_account_id: next } : {};
        const r = await api.get('/banking/code-dictionary', { params });
        setEntries(r.data?.entries || []);
      } catch { /* non-fatal */ }
    })();
  };

  const addOrUpdate = async () => {
    const code = newCode.trim().toUpperCase();
    const label = newLabel.trim();
    if (!code || !label) {
      toast.error('Code and label are both required');
      return;
    }
    setSaving(true);
    try {
      // iter446b — when a bank scope is active, save the new entry as
      // bank-scoped so it only applies to that bank's statements.
      const body = bankScope
        ? { code, label, bank_account_id: bankScope }
        : { code, label };
      await api.post('/banking/code-dictionary/add', body);
      toast.success(bankScope
        ? `Saved ${code} → ${label} (this bank only)`
        : `Saved ${code} → ${label}`);
      setNewCode('');
      setNewLabel('');
      await fetchAll();
      // Refresh trending — the just-saved code should drop off the list.
      fetchTrending();
      onDirty?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save code');
    } finally {
      setSaving(false);
    }
  };

  const teachToken = async (token, sample) => {
    setNewCode(token);
    setNewLabel('');
    // Focus the label input by selecting it.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="bank-code-new-label"]');
      if (el) { el.focus(); el.select?.(); }
    }, 50);

    if (aiSuggest) {
      setAiBusy(true);
      try {
        const r = await api.post('/banking/code-dictionary/ai-suggest-label', { token, sample });
        if (r.data?.label) {
          setNewLabel(r.data.label);
          toast.success(`AI suggested: ${r.data.label} — edit and Save when ready`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'AI label suggestion failed');
      } finally {
        setAiBusy(false);
      }
    } else if (sample) {
      // Manual mode: just show a context hint.
      toast.info(`Editing "${token}" — seen in: ${sample.slice(0, 60)}${sample.length > 60 ? '…' : ''}`);
    }
  };

  const migrateToBank = async (code, bankId) => {
    if (!code || !bankId) return;
    setSaving(true);
    try {
      await api.post('/banking/code-dictionary/migrate-to-bank', {
        code,
        bank_account_id: bankId,
      });
      toast.success(`Moved ${code} → ${bankLabel(bankId)}`);
      await fetchAll();
      onDirty?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Migration failed');
    } finally {
      setSaving(false);
    }
  };


  const removeOverride = async (code, scopeBankId) => {
    setSaving(true);
    try {
      const url = scopeBankId
        ? `/banking/code-dictionary/${encodeURIComponent(code)}?bank_account_id=${encodeURIComponent(scopeBankId)}`
        : `/banking/code-dictionary/${encodeURIComponent(code)}`;
      await api.delete(url);
      toast.success(`Removed override for ${code}`);
      await fetchAll();
      onDirty?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove override');
    } finally {
      setSaving(false);
    }
  };

  const filtered = entries.filter(e => {
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return e.code.toLowerCase().includes(q) || (e.label || '').toLowerCase().includes(q);
  });

  // iter445d — Suggest-all: fan out one batch LLM call covering every
  // visible trending token, then render an inline editable preview.
  const suggestAll = async () => {
    if (!trending.length) return;
    setBatchBusy(true);
    setBatchRows([]);
    try {
      const items = trending.map(t => ({ token: t.token, sample: t.sample }));
      const r = await api.post('/banking/code-dictionary/ai-suggest-labels-batch', { items });
      const rows = (r.data?.results || []).map(x => ({
        token: x.token,
        label: x.label || '',
        picked: !!x.ok && !!x.label,
        ok: !!x.ok,
        // iter463 — AI suggester pre-flags obvious POS-settlement codes
        // ("MADA SALE", "POS SETTLEMENT", etc.). User can untick before
        // saving if the heuristic over-flagged.
        suggest_pos: !!x.suggest_pos_settlement,
        // iter464 — Same for refund / chargeback codes.
        suggest_refund: !!x.suggest_refund,
      }));
      setBatchRows(rows);
      const okCount = rows.filter(r => r.ok).length;
      toast.success(`AI suggested labels for ${okCount}/${rows.length} tokens — review & save`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Batch suggestion failed');
    } finally {
      setBatchBusy(false);
    }
  };

  const updateBatchRow = (idx, patch) => {
    setBatchRows(rows => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const saveBatchPicks = async () => {
    const picks = batchRows.filter(r => r.picked && r.label.trim());
    if (!picks.length) {
      toast.error('Tick at least one row with a non-empty label');
      return;
    }
    setSaving(true);
    let okN = 0;
    let failN = 0;
    let posN = 0;
    let refundN = 0;
    for (const row of picks) {
      try {
        // iter446b — propagate the active bank scope when saving so
        // batch picks land as bank-specific overrides if the trending
        // list is scoped to a single bank.
        const body = bankScope
          ? { code: row.token, label: row.label.trim(), bank_account_id: bankScope }
          : { code: row.token, label: row.label.trim() };
        // eslint-disable-next-line no-await-in-loop
        await api.post('/banking/code-dictionary/add', body);
        okN += 1;
        // iter463 — Also persist the POS-settlement flag when the AI
        // suggested it (and the user kept the checkbox ticked).
        if (row.suggest_pos) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await api.put(`/banking/code-dictionary/pos-settlement/${encodeURIComponent(row.token)}`, { flagged: true });
            posN += 1;
          } catch { /* non-fatal; the label still saved */ }
        }
        // iter464 — Same for the refund flag.
        if (row.suggest_refund) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await api.put(`/banking/code-dictionary/refund/${encodeURIComponent(row.token)}`, { flagged: true });
            refundN += 1;
          } catch { /* non-fatal */ }
        }
      } catch {
        failN += 1;
      }
    }
    setSaving(false);
    const scopeHint = bankScope ? ' (this bank only)' : '';
    const flagParts = [];
    if (posN) flagParts.push(`${posN} POS`);
    if (refundN) flagParts.push(`${refundN} refund`);
    const posHint = flagParts.length ? ` · flagged ${flagParts.join(' + ')}` : '';
    if (okN) toast.success(`Saved ${okN} code${okN === 1 ? '' : 's'}${scopeHint}${posHint}${failN ? ` (${failN} failed)` : ''}`);
    if (failN && !okN) toast.error(`Failed to save ${failN} code${failN === 1 ? '' : 's'}`);
    setBatchRows([]);
    await fetchAll();
    await fetchTrending();
    onDirty?.();
  };

  const sourceBadge = (src) => {
    if (src === 'bank-override') return <Badge className="text-[9px] bg-violet-100 text-violet-800 border-violet-300">bank-only override</Badge>;
    if (src === 'bank-custom') return <Badge className="text-[9px] bg-violet-100 text-violet-800 border-violet-300">bank-only custom</Badge>;
    if (src === 'override') return <Badge className="text-[9px] bg-amber-100 text-amber-800 border-amber-300">overridden</Badge>;
    if (src === 'custom') return <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border-emerald-300">custom</Badge>;
    return <Badge variant="outline" className="text-[9px] text-muted-foreground">default</Badge>;
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="bank-code-dict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen size={18} className="text-sky-600" />
            Bank-statement Code Dictionary
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Teach Smarto what bank shorthand means. Custom entries shadow built-in defaults and are scoped to your tenant.
          </p>
        </DialogHeader>

        {/* Add / update */}
        <div className="rounded-md border bg-sky-50/50 p-3 space-y-2" data-testid="bank-code-add-row">
          <div className="flex items-center gap-2 text-xs font-medium text-sky-900">
            <Sparkles size={12} /> Add or update a code
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              placeholder='Code (e.g. "SARIE-OUT")'
              className="h-9 text-xs sm:max-w-[200px] font-mono uppercase"
              data-testid="bank-code-new-code"
            />
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={aiBusy ? 'AI is thinking…' : 'Friendly label (e.g. "Outgoing SARIE transfer")'}
              className="h-9 text-xs flex-1"
              data-testid="bank-code-new-label"
              disabled={aiBusy}
            />
            <Button size="sm" onClick={addOrUpdate} disabled={saving}
              className="bg-sky-600 hover:bg-sky-700 text-white whitespace-nowrap"
              data-testid="bank-code-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
              Save
            </Button>
          </div>
        </div>

        {/* iter445 — Trending unknown codes */}
        {(trendingLoading || trending.length > 0 || bankScope) && (
          <div className="rounded-md border bg-amber-50/60 p-3 space-y-2" data-testid="bank-code-trending">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-xs font-medium text-amber-900 flex-wrap">
                <TrendingUp size={12} /> Trending unknown codes in your statements
                {trendingLoading && <Loader2 size={11} className="animate-spin opacity-60" />}
                {bankAccounts.length > 1 && (
                  <select
                    value={bankScope}
                    onChange={(e) => onBankScopeChange(e.target.value)}
                    className="h-6 rounded border border-amber-300 bg-white text-[10px] px-1 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    data-testid="bank-code-trending-scope">
                    <option value="">All banks</option>
                    {bankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.bank_name || a.account_name || a.account_number || a.id.slice(0, 8)}</option>
                    ))}
                  </select>
                )}
              </div>
              <label className="flex items-center gap-1.5 text-[10px] text-amber-900 cursor-pointer select-none"
                title="When ON, clicking a chip asks an LLM (claude-haiku via Emergent LLM Key) for a label suggestion."
                data-testid="bank-code-ai-toggle-label">
                <Wand2 size={11} className={aiBusy ? 'animate-pulse text-violet-600' : aiSuggest ? 'text-violet-600' : 'text-muted-foreground'} />
                <span className="whitespace-nowrap">AI suggest label</span>
                <Switch
                  checked={aiSuggest}
                  onCheckedChange={toggleAi}
                  className="data-[state=checked]:bg-violet-600"
                  data-testid="bank-code-ai-toggle"
                />
              </label>
            </div>
            {!trendingLoading && trending.length === 0 ? (
              <div className="text-[11px] text-muted-foreground">Smarto already understands every code in your recent statements — nothing to teach right now.</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-1.5">
                  {trending.map((tok) => (
                    <button
                      key={tok.token}
                      type="button"
                      onClick={() => teachToken(tok.token, tok.sample)}
                      disabled={aiBusy || batchBusy}
                      title={`Seen ${tok.count}× in: ${tok.sample}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-amber-300 hover:bg-amber-100 hover:border-amber-400 text-[11px] transition-colors disabled:opacity-50 disabled:cursor-wait"
                      data-testid={`bank-code-trending-${tok.token}`}>
                      <span className="font-mono font-semibold text-amber-900">{tok.token}</span>
                      <span className="text-[10px] text-muted-foreground">×{tok.count}</span>
                    </button>
                  ))}
                  {aiSuggest && trending.length > 1 && (
                    <Button
                      size="sm"
                      onClick={suggestAll}
                      disabled={batchBusy || aiBusy}
                      className="h-6 px-2 ml-1 bg-violet-600 hover:bg-violet-700 text-white text-[11px]"
                      data-testid="bank-code-suggest-all-btn">
                      {batchBusy ? <Loader2 size={11} className="animate-spin mr-1" /> : <Wand2 size={11} className="mr-1" />}
                      Suggest all
                    </Button>
                  )}
                </div>

                {/* Batch preview — editable rows with checkboxes */}
                {batchRows.length > 0 && (
                  <div className="mt-2 rounded border bg-white p-2 space-y-1.5" data-testid="bank-code-batch-preview">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-medium text-violet-900">
                        Review AI suggestions — edit labels and tick rows to save
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setBatchRows([])}
                        className="h-6 px-2 text-[11px]"
                        data-testid="bank-code-batch-cancel-btn">
                        Cancel
                      </Button>
                    </div>
                    <div className="divide-y divide-border max-h-44 overflow-y-auto">
                      {batchRows.map((row, i) => (
                        <div key={row.token} className="flex items-center gap-2 py-1"
                          data-testid={`bank-code-batch-row-${row.token}`}>
                          <input
                            type="checkbox"
                            checked={row.picked}
                            onChange={(e) => updateBatchRow(i, { picked: e.target.checked })}
                            className="accent-violet-600"
                            data-testid={`bank-code-batch-check-${row.token}`}
                          />
                          <span className="font-mono text-[11px] font-semibold text-foreground min-w-[100px]">{row.token}</span>
                          {row.ok ? (
                            <>
                              <Input
                                value={row.label}
                                onChange={(e) => updateBatchRow(i, { label: e.target.value, picked: true })}
                                className="h-7 text-[11px] flex-1"
                                data-testid={`bank-code-batch-label-${row.token}`}
                              />
                              {/* iter463 — POS-Settlement pre-flag from AI heuristic */}
                              <label
                                className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                                title="When checked, this code's credits count toward POS bank-sales in the Sales-vs-Bank widget"
                              >
                                <input
                                  type="checkbox"
                                  checked={!!row.suggest_pos}
                                  onChange={(e) => updateBatchRow(i, { suggest_pos: e.target.checked })}
                                  className="h-3 w-3 accent-info"
                                  data-testid={`bank-code-batch-pos-${row.token}`}
                                />
                                <span>POS</span>
                              </label>
                              {/* iter464 — Refund pre-flag from AI heuristic */}
                              <label
                                className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                                title="When checked, this code's credits are subtracted from the bank-credit total (treats them as refunds / chargebacks)"
                              >
                                <input
                                  type="checkbox"
                                  checked={!!row.suggest_refund}
                                  onChange={(e) => updateBatchRow(i, { suggest_refund: e.target.checked })}
                                  className="h-3 w-3 accent-rose-500"
                                  data-testid={`bank-code-batch-refund-${row.token}`}
                                />
                                <span>Refund</span>
                              </label>
                            </>
                          ) : (
                            <span className="flex-1 text-[11px] text-muted-foreground italic">AI couldn&apos;t suggest a label — click the chip to edit manually</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button size="sm" onClick={saveBatchPicks} disabled={saving}
                        className="h-7 bg-violet-600 hover:bg-violet-700 text-white text-[11px]"
                        data-testid="bank-code-batch-save-btn">
                        {saving ? <Loader2 size={11} className="animate-spin mr-1" /> : <Plus size={11} className="mr-1" />}
                        Save selected
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
            <p className="text-[10px] text-muted-foreground">
              {aiSuggest
                ? 'AI is ON — clicking a chip will pre-fill the label with claude-haiku\'s best guess (review & edit before Save).'
                : 'Click a token to teach Smarto what it means — the label field above will focus so you can name it.'}
            </p>
          </div>
        )}

        {/* Filter + list */}
        <div className="space-y-2">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by code or label…"
            className="h-8 text-xs"
            data-testid="bank-code-filter"
          />
          <div className="border rounded-md max-h-80 overflow-y-auto divide-y divide-border">
            {loading ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                <Loader2 size={16} className="animate-spin inline-block mr-2" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                {filter ? 'No codes match this filter' : 'Dictionary is empty'}
              </div>
            ) : filtered.map((e) => (
              <div key={`${e.code}-${e.bank_account_id || 'tw'}`} className="px-3 py-1.5 flex items-center gap-2 text-xs hover:bg-muted/40"
                data-testid={`bank-code-row-${e.code}`}>
                <span className="font-mono font-semibold text-foreground min-w-[110px]">{e.code}</span>
                <span className="flex-1 truncate text-muted-foreground" title={e.label}>{e.label || '—'}</span>
                {/* iter447 — tiny "Scope" indicator: bank label or "All banks" */}
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap ${
                    e.bank_account_id
                      ? 'bg-violet-50 text-violet-800 border-violet-300'
                      : 'bg-slate-50 text-slate-600 border-slate-200'
                  }`}
                  title={e.bank_account_id ? `Applies only to: ${bankLabel(e.bank_account_id)}` : 'Applies to every bank'}
                  data-testid={`bank-code-scope-${e.code}`}>
                  {e.bank_account_id ? bankLabel(e.bank_account_id) : 'All banks'}
                </span>
                {sourceBadge(e.source)}
                {/* iter463 — POS-settlement flag. Tick to count this
                    code's credits toward POS bank-sales in the
                    Sales-vs-Bank widget. */}
                <label
                  className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                  title="Treat credits with this code as POS settlements for the Sales-vs-Bank reconciliation"
                >
                  <input
                    type="checkbox"
                    checked={!!e.is_pos_settlement}
                    onChange={(ev) => togglePos(e.code, ev.target.checked)}
                    className="h-3 w-3 accent-info"
                    data-testid={`bank-code-pos-${e.code}`}
                  />
                  <span>POS</span>
                </label>
                {/* iter464 — Refund / chargeback flag. Subtracted from
                    bank-credit total so refunds don't inflate variance. */}
                <label
                  className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                  title="Treat credits with this code as refunds / chargebacks — subtracted from POS settlement total"
                >
                  <input
                    type="checkbox"
                    checked={!!e.is_refund}
                    onChange={(ev) => toggleRefund(e.code, ev.target.checked)}
                    className="h-3 w-3 accent-rose-500"
                    data-testid={`bank-code-refund-${e.code}`}
                  />
                  <span>Refund</span>
                </label>
                {/* iter447 — Migrate-to-bank action (only for tenant-wide overrides) */}
                {(e.source === 'override' || e.source === 'custom') && bankAccounts.length > 0 && (
                  <select
                    onChange={(ev) => { if (ev.target.value) migrateToBank(e.code, ev.target.value); ev.target.value = ''; }}
                    defaultValue=""
                    className="h-6 text-[10px] rounded border border-violet-200 bg-white text-violet-700 px-1"
                    title="Move this tenant-wide override into one bank's scope"
                    data-testid={`bank-code-migrate-${e.code}`}>
                    <option value="">→ Scope to…</option>
                    {bankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.bank_name || a.account_name || a.account_number || a.id.slice(0, 8)}</option>
                    ))}
                  </select>
                )}
                {(e.source === 'override' || e.source === 'custom' || e.source === 'bank-override' || e.source === 'bank-custom') && (
                  <Button size="sm" variant="ghost"
                    className="h-6 w-6 p-0 text-error hover:text-error hover:bg-error/10"
                    onClick={() => removeOverride(e.code, e.bank_account_id || null)}
                    disabled={saving}
                    title={
                      e.source === 'bank-override' || e.source === 'bank-custom'
                        ? 'Remove this bank-specific override'
                        : e.source === 'override' ? 'Restore the built-in default' : 'Delete this custom code'
                    }
                    data-testid={`bank-code-delete-${e.code}`}>
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {entries.filter(e => e.source !== 'default').length} custom / {entries.length} total
          </div>
        </div>

        {/* iter447 — Bank-scoped overrides accordion */}
        {(Object.keys(byBank || {}).length > 0 || entries.some(e => e.source === 'override' || e.source === 'custom')) && (
          <details className="rounded-md border bg-violet-50/40" data-testid="bank-code-bank-accordion">
            <summary className="px-3 py-2 text-xs font-medium text-violet-900 cursor-pointer select-none flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <BookOpen size={12} />
                All overrides grouped by scope
              </span>
              <span className="text-[10px] text-muted-foreground">
                {entries.filter(e => e.source === 'override' || e.source === 'custom').length} tenant-wide
                {' · '}
                {Object.entries(byBank).reduce((n, [, m]) => n + Object.keys(m || {}).length, 0)} per-bank
                {' across '}
                {Object.keys(byBank).length} bank{Object.keys(byBank).length === 1 ? '' : 's'}
              </span>
            </summary>
            <div className="p-2 space-y-2">
              {/* iter448 — Tenant-wide group */}
              {entries.some(e => e.source === 'override' || e.source === 'custom') && (
                <div className="rounded border bg-white" data-testid="bank-code-accordion-tenant-wide">
                  <div className="px-2 py-1 bg-amber-100/60 text-[11px] font-semibold text-amber-900 flex items-center justify-between">
                    <span>Tenant-wide (applies to every bank)</span>
                    <span className="text-[10px] text-muted-foreground">
                      {entries.filter(e => e.source === 'override' || e.source === 'custom').length} code{entries.filter(e => e.source === 'override' || e.source === 'custom').length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {entries
                      .filter(e => e.source === 'override' || e.source === 'custom')
                      .map(e => (
                        <div key={e.code} className="px-2 py-1 flex items-center gap-2 text-[11px]"
                          data-testid={`bank-code-accordion-tw-row-${e.code}`}>
                          <span className="font-mono font-semibold min-w-[100px]">{e.code}</span>
                          <span className="flex-1 truncate text-muted-foreground" title={e.label}>{e.label}</span>
                          <Button size="sm" variant="ghost"
                            className="h-6 w-6 p-0 text-error hover:text-error hover:bg-error/10"
                            onClick={() => removeOverride(e.code, null)}
                            disabled={saving}
                            title="Remove this tenant-wide override"
                            data-testid={`bank-code-accordion-tw-delete-${e.code}`}>
                            <Trash2 size={11} />
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Per-bank groups */}
              {Object.entries(byBank).map(([bid, codeMap]) => (
                <div key={bid} className="rounded border bg-white">
                  <div className="px-2 py-1 bg-violet-100/60 text-[11px] font-semibold text-violet-900 flex items-center justify-between"
                    data-testid={`bank-code-accordion-section-${bid}`}>
                    <span>{bankLabel(bid)}</span>
                    <span className="text-[10px] text-muted-foreground">{Object.keys(codeMap || {}).length} code{Object.keys(codeMap || {}).length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="divide-y divide-border">
                    {Object.entries(codeMap || {}).map(([code, label]) => (
                      <div key={code} className="px-2 py-1 flex items-center gap-2 text-[11px]">
                        <span className="font-mono font-semibold min-w-[100px]">{code}</span>
                        <span className="flex-1 truncate text-muted-foreground" title={label}>{label}</span>
                        <Button size="sm" variant="ghost"
                          className="h-6 w-6 p-0 text-error hover:text-error hover:bg-error/10"
                          onClick={() => removeOverride(code, bid)}
                          disabled={saving}
                          title={`Remove this override for ${bankLabel(bid)}`}
                          data-testid={`bank-code-accordion-delete-${bid}-${code}`}>
                          <Trash2 size={11} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} data-testid="bank-code-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
